import {Map} from 'immutable';
import {PrerequisiteDetails} from 'epic-graphql-types';
import dieselReducer from '../../decorators/DieselReducer';
import {
    ProductSettings,
    FetchBulkProductOfferOwnedStatusPayload,
    FetchProductOfferOwnedStatusSuccessPayload,
    FetchProductOfferOwnedStatusFailedPayload,
    EntitlementsPayload,
    GlobalAutoUpdatePayload,
    ProductAction,
    ProductInfoPayload,
    ProductNamespaceMappingPayload,
    ProductsInfoPayload,
    SetProductButtonDisabledPayload,
    ProductDLCConfigPayload,
    ProductTypes,
    ProductPrerequisitePayload,
    ProductSettingsPayload,
    SetProductAutoUpdatePayload,
    SetProductCloudSavePayload
} from '../actions/product.actions';
import {ProductInstallState, DLCState} from '../../bridges/productBridge';
import {CatalogId, Namespace, ProductName} from '../../commonTypes';
import {CatalogOffer} from '../api/CatalogApi';

export type InstallStatesMap = Map<CatalogId, ProductInstallState>;
export type UserBuildEntitlementsMap = Map<CatalogId, boolean>;
export type UserOfferEntitlements = Map<CatalogId, boolean>;
export type NamespaceMappingMap = Map<Namespace, ProductName>;
export type ButtonDisabledSettingsMap = Map<CatalogId, boolean>;
export type ProductSettingsMap = Map<CatalogId, ProductSettings>;
export type ProductDLCConfigsMap = Map<CatalogId, DLCState>;
export type BuildPrerequisitesMap = Map<string, boolean>;

interface ProductStateProps {
    installStates: InstallStatesMap;
    userBuildEntitlements: UserBuildEntitlementsMap;
    userOfferEntitlements: UserOfferEntitlements;
    namespaceMapping: NamespaceMappingMap;
    buttonDisabledSettings: ButtonDisabledSettingsMap;
    globalAutoUpdateSetting: boolean;
    productDLCConfigs: ProductDLCConfigsMap;
    buildPrerequisites: BuildPrerequisitesMap;
    productSettings: ProductSettingsMap;
}

export type ProductState = Map<keyof ProductStateProps, ProductStateProps[keyof ProductStateProps]>;

export const DEFAULT_STATE: ProductState = Map({
    installStates: Map(),
    userBuildEntitlements: Map(),
    userOfferEntitlements: Map(),
    namespaceMapping: Map(),
    buttonDisabledSettings: Map(),
    globalAutoUpdateSetting: false,
    productDLCConfigs: Map(),
    buildPrerequisites: Map(),
    productSettings: Map()
}) as ProductState;

const updateProductButtonDisabled = (
    state: ProductState,
    catalogId: CatalogId,
    disabled: boolean
): ProductState => {
    if (!catalogId) return state;
    return state.update('buttonDisabledSettings', (value: ButtonDisabledSettingsMap) =>
        value.set(catalogId, disabled)
    );
};

const bulkUpdateProductSettings = (
    state: ProductState,
    settings: ProductSettings[]
): ProductState => {
    if (!settings || !settings.length) return state;
    return state.withMutations((newState: ProductState) =>
        settings.map((item: ProductSettings) =>
            newState.setIn(['productSettings', item.catalogId], item)
        )
    );
};

const updateProductInstallState = (
    state: ProductState,
    installState: ProductInstallState
): ProductState => {
    if (!installState || !installState.catalogid) return state;
    return state.update('installStates', (value: InstallStatesMap) =>
        value.set(installState.catalogid, installState)
    );
};

const areConfigsEqual = (configs: DLCState[], configsMap: ProductDLCConfigsMap): boolean =>
    configs.every((item: DLCState): boolean => {
        const lastItem: DLCState = configsMap.get(item.catalogid);
        return (
            lastItem &&
            item.catalogid === lastItem.catalogid &&
            item.bshouldbeinstalled === lastItem.bshouldbeinstalled
        );
    });

export const productReducer = (
    state: ProductState = DEFAULT_STATE,
    {type, payload}: ProductAction
): ProductState => {
    switch (type) {
        case ProductTypes.FETCH_PRODUCT_INSTALL_DETAILS_SUCCESS: {
            const {product} = payload as ProductInfoPayload;
            return updateProductInstallState(state, product);
        }

        case ProductTypes.BULK_FETCH_PRODUCT_INSTALL_DETAILS_SUCCESS: {
            const {products} = payload as ProductsInfoPayload;
            return state.withMutations(newState => {
                products.map((product: ProductInstallState) =>
                    updateProductInstallState(newState, product)
                );
            });
        }

        case ProductTypes.BULK_FETCH_PRODUCT_BUILD_ENTITLEMENTS_SUCCESS: {
            const {entitlements} = payload as EntitlementsPayload;
            return state.withMutations(newState =>
                entitlements.map(entitlement =>
                    newState.update('userBuildEntitlements', (value: UserBuildEntitlementsMap) =>
                        value.set(entitlement.catalogId, entitlement.entitledToBuild)
                    )
                )
            );
        }

        case ProductTypes.FETCH_PRODUCT_GLOBAL_AUTO_UPDATE_SUCCESS: {
            const {globalAutoUpdate} = payload as GlobalAutoUpdatePayload;
            return state.set('globalAutoUpdateSetting', globalAutoUpdate);
        }

        case ProductTypes.BULK_FETCH_PRODUCT_SETTINGS_SUCCESS: {
            const {settings} = payload as ProductSettingsPayload;
            return bulkUpdateProductSettings(state, settings);
        }

        case ProductTypes.SET_PRODUCT_AUTO_UPDATE: {
            const {catalogId, autoUpdate} = payload as SetProductAutoUpdatePayload;
            // make a copy
            const settings: ProductSettings = {
                ...state.getIn(['productSettings', catalogId]),
                catalogId,
                autoUpdate
            };
            return bulkUpdateProductSettings(state, [settings]);
        }

        case ProductTypes.SET_PRODUCT_CLOUD_SAVE: {
            const {catalogId, cloudSave} = payload as SetProductCloudSavePayload;
            // make a copy
            const settings: ProductSettings = {
                ...state.getIn(['productSettings', catalogId]),
                catalogId,
                cloudSave
            };
            return bulkUpdateProductSettings(state, [settings]);
        }

        case ProductTypes.SET_PRODUCT_BUTTON_DISABLED: {
            const {catalogId, disabled} = payload as SetProductButtonDisabledPayload;
            return updateProductButtonDisabled(state, catalogId, disabled);
        }

        case ProductTypes.FETCH_PRODUCT_NAMESPACE_MAPPING_DATA_SUCCESS: {
            const {namespaceMapping} = payload as ProductNamespaceMappingPayload;
            return state.set('namespaceMapping', Map(namespaceMapping));
        }

        case ProductTypes.BULK_FETCH_PRODUCT_OFFER_OWNED_STATUS: {
            const {offers} = payload as FetchBulkProductOfferOwnedStatusPayload;
            return state.withMutations(newState =>
                offers.map((offer: CatalogOffer) =>
                    newState.setIn(['userOfferEntitlements', offer.id], Map({isLoading: true}))
                )
            );
        }

        case ProductTypes.FETCH_PRODUCT_OFFER_OWNED_STATUS_SUCCESS: {
            const {offerId, ownedStatus} = payload as FetchProductOfferOwnedStatusSuccessPayload;
            return state.setIn(
                ['userOfferEntitlements', offerId],
                Map({
                    ...ownedStatus,
                    isLoading: false
                })
            );
        }

        case ProductTypes.FETCH_PRODUCT_OFFER_OWNED_STATUS_FAILED: {
            const {offerId} = payload as FetchProductOfferOwnedStatusFailedPayload;
            return state.setIn(['userOfferEntitlements', offerId], Map({isLoading: false}));
        }

        case ProductTypes.FETCH_PRODUCT_DLC_CONFIGURATION_SUCCESS: {
            const {config} = payload as ProductDLCConfigPayload;
            // check to see if the stored data has changed
            if (areConfigsEqual(config, state.get('productDLCConfigs') as ProductDLCConfigsMap)) {
                return state;
            }
            return state.set(
                'productDLCConfigs',
                config.reduce(
                    (acc: Map<CatalogId, DLCState>, item: DLCState) =>
                        acc.set(item.catalogid, item),
                    Map()
                )
            );
        }

        case ProductTypes.BULK_FETCH_PRODUCT_PREREQUISITES_SUCCESS: {
            const {prerequisites} = payload as ProductPrerequisitePayload;
            if (!prerequisites || !prerequisites.length) return state;

            return state.withMutations(newState => {
                prerequisites.forEach((p: PrerequisiteDetails) => {
                    newState.setIn(
                        ['buildPrerequisites', p.offerId],
                        p.satisfiesPrerequisites || false
                    );
                });
                return newState;
            });
        }

        default:
            return state;
    }
};

export default dieselReducer('product_v2', productReducer);
